import React from 'react';
import { X, FileText, BarChart3, Table as TableIcon } from 'lucide-react';
import { DataProcessingResponse, TextOutput, ChartOutput, TableOutput } from './types';
import { ChartViewer } from './viewers/ChartViewer';
import { TextViewer } from './viewers/TextViewer';
import { TableViewer } from './viewers/TableViewer';

interface ProcessedResultViewerProps {
    result: DataProcessingResponse;
    onClose: () => void;
}

export const ProcessedResultViewer: React.FC<ProcessedResultViewerProps> = ({ result, onClose }) => {
    const getIcon = () => {
        switch (result.outputType) {
            case 'chart':
                return <BarChart3 size={24} className="text-purple-600" />;
            case 'table':
                return <TableIcon size={24} className="text-purple-600" />;
            default:
                return <FileText size={24} className="text-purple-600" />;
        }
    };

    const getTitle = () => {
        switch (result.outputType) {
            case 'chart':
                return (result.data as ChartOutput).title || 'Visualisering';
            case 'table':
                return (result.data as TableOutput).title || 'Tabell';
            case 'text':
                return 'Analyse';
            default:
                return 'Resultat';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b bg-purple-50">
                    <div className="flex items-center gap-3">
                        {getIcon()}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">
                                {getTitle()}
                            </h2>
                            {result.explanation && (
                                <p className="text-sm text-gray-600 mt-1">
                                    {result.explanation}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500">
                            <span>{result.executionTimeMs}ms</span>
                            {result.tokensUsed && (
                                <>
                                    <span className="mx-2">•</span>
                                    <span>{result.tokensUsed} tokens</span>
                                </>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-6">
                    {result.outputType === 'chart' && (
                        <ChartViewer data={result.data as ChartOutput} />
                    )}
                    {result.outputType === 'text' && (
                        <TextViewer data={result.data as TextOutput} />
                    )}
                    {result.outputType === 'table' && (
                        <TableViewer data={result.data as TableOutput} />
                    )}
                </div>
            </div>
        </div>
    );
};
